import {
	Vue, Component, Watch, Prop, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import { namespace } from 'vuex-facing-decorator';
import { getModule } from 'vuex-module-decorators';
import mitt from 'mitt';
import OffTable from 'store/offerings/offeringTable';
import TabulatorSwitchBox from 'components/Tabulator/TabulatorSwitchBox';
import { TabEvent } from 'interfaces/app';
import {
	CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator,
} from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import { nextTick } from 'vue';
import { useStore } from 'vuex';
import CustomSelect from 'components/CustomSelect';
import BadgeOfferingService from 'services/BadgeOfferingService';
import OfferingService from 'services/OfferingService';
import Template from './template.vue';

const OfferingsTable = namespace('OfferingTable');

interface OfferingWithGroupName extends DB.OfferingModel {
	groupname: string;
}

const eventBus = mitt<TabEvent<DB.OfferingModel>>();
@Component({
	components: {
		TabulatorSwitchBox,
		CustomSelect,
	},
	mixins: [Template],
})
class OfferingTable extends Vue {
	@Ref('badgeoffering')
	private readonly tableReference!: HTMLDivElement;

	private isLoaded = false;

	private pageOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100, 200, 300, 400, 500];

	private checkOffering: Record<string, DB.BadgeOfferingModel> = {};

	private columnDefs: ColumnDefinitionExtended[] = [];

	private table?: Tabulator;

	private perPage = 20;

	private rowData: DB.OfferingModel[] | OfferingWithGroupName[] = [];

	private search = null;

	private store = useStore();

	@Prop({
		type: String,
	})
	public readonly routeId!: string;

	@OfferingsTable.Getter
	private getGroupName!: Array<DB.ProductGroupModel>;

	@OfferingsTable.Getter
	protected getJustGroupName!: Array<string>;

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	public beforeMount(): void {
		this.columnDefs = [
			{ field: 'id', title: 'Offering ID' },
			{
				title: 'External ID',
				field: 'externalid',
			},
			{
				title: 'Name',
				formatter: (cell: CellComponentExtended<DB.OfferingModel>) => {
					const data = cell.getData();
					const name = document.createElement('div');
					name.textContent = `${data.name} ${data.size}`;

					const description = document.createElement('div');
					description.textContent = data.description ?? null;

					const container = document.createElement('div');
					container.appendChild(name);
					container.appendChild(description);

					return container;
				},
			},
			{
				title: 'Available',
				field: 'url',
				titleFormatter: () => {
					const instance = createInstance({
						component: TabulatorSwitchBox,
						props: {
							checked: false,
							text: 'Available in Region',
							eventName: 'headerSwitchBoxChanged',
							eventBus,
						},
					});

					return instance;
				},
				formatter: (cell: CellComponentExtended<DB.OfferingModel>) => {
					const data = cell.getData();
					const instance = createInstance({
						component: TabulatorSwitchBox,
						props: {
							checked: Boolean(data && this.checkOffering[data.id]),
							data,
							eventBus,
						},
					});

					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	public mounted(): void {
		eventBus.on(
			'switchBoxChanged',
			this.switchBoxChanged,
		);
		eventBus.on(
			'headerSwitchBoxChanged',
			this.headerSwitchBoxChanged,
		);
		getModule(
			OffTable,
			this.store,
		);
		nextTick(() => {
			this.store.dispatch('OfferingTable/getGroupNameAction');
		});
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
		this.table.on(
			'tableBuilt',
			() => {
				this.getData();
			},
		);
	}

	private switchBoxChanged(data: TabEvent<DB.OfferingModel>['switchBoxChanged']): void {
		this.table?.alert('Loading');
		if (data.event) {
			BadgeOfferingService
				.create(
					{
						offeringid: data.params.id,
						badgeid: parseInt(
							this.routeId,
							10,
						),
					},
				)
				.then((response) => {
					this.checkOffering[response.offeringid] = response;
					const allChecked = this.table?.getData().every((item) => this.checkOffering[item.id]);
					this.updateHeaderBox(allChecked as boolean);
					this.table?.scrollToRow(response.offeringid);
					return undefined;
				})
				.finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
		} else {
			BadgeOfferingService
				.delete(this.checkOffering[data.params.id].id)
				.then(() => {
					delete this.checkOffering[data.params.id];
					const allChecked = this.table?.getData().every((item) => this.checkOffering[item.id]);
					this.updateHeaderBox(allChecked as boolean);
					this.table?.scrollToRow(data.params.id);
					return undefined;
				})
				.finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
		}
	}

	// This function checks/unchecks all of the switchboxes in the grid row and updates the checkOffering object
	private headerSwitchBoxChanged(data: TabEvent<DB.OfferingModel>['headerSwitchBoxChanged']): void {
		this.isLoaded = true;
		const allOfferingid: number[] = [];
		this.table?.getData().map((node) => {
			if (node) {
				allOfferingid.push(node.id);
			}
			return undefined;
		});

		const offeringid = Object.values(this.checkOffering)
			.filter((item) => allOfferingid.includes(item.offeringid))
			.map((item) => item.id);

		const parameter = new URLSearchParams({
			where: JSON.stringify({
				id: offeringid,
			}),
		});

		// Create postData array, but only include offering IDs that are not checked
		const postData = allOfferingid
			.filter((offeringId) => !Object.keys(this.checkOffering).includes(offeringId.toString()))
			.map((offeringId) => ({
				offeringid: offeringId,
				badgeid: parseInt(
					this.routeId,
					10,
				),
			}));

		(data.event ? BadgeOfferingService.create(postData) : BadgeOfferingService.deleteWhere(parameter))
			.then((response) => {
				if (data.event) {
					const resp = response as DB.BadgeOfferingModel[];
					resp.forEach((item: DB.BadgeOfferingModel) => {
						this.checkOffering[item.offeringid] = item;
						this.table?.redraw(true);
					});
				} else {
					allOfferingid.forEach((id) => {
						delete this.checkOffering[id];
						this.table?.redraw(true);
					});
				}
				return undefined;
			})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$toastError(err.message);
			});
	}

	// This function updates the header formatter switchbox anytime the table renders or redrawn
	private updateHeaderBox(updateCellRenderer: boolean): void {
		const columnDef = this.table?.getColumnDefinitions().map((column) => {
			if (column.field === 'url') {
				return {
					...column,
					title: 'Available',
					field: 'url',
					titleFormatter: updateCellRenderer ? () => {
						const allChecked = this.table?.getData().every((item) => this.table?.getData().length !== 0 && this.checkOffering[item.id]);
						const instance = createInstance({
							component: TabulatorSwitchBox,
							props: {
								checked: Boolean(allChecked && this.table?.getData().length !== 0),
								text: 'Available in Region',
								eventName: 'headerSwitchBoxChanged',
								eventBus,
							},
						});

						return instance;
					} : column.titleFormatter,
					formatter: (cell: CellComponentExtended<DB.OfferingModel>) => {
						const data = cell.getData();
						const instance = createInstance({
							component: TabulatorSwitchBox,
							props: {
								checked: Boolean(data && this.checkOffering[data.id]),
								data,
								eventBus,
							},
						});

						return instance;
					},
				};
			}
			return column;
		}) as ColumnDefinitionExtended[];

		// update the column definitions
		this.table?.setColumns(columnDef);
	}

	public beforeUnmount() {
		eventBus.off(
			'switchBoxChanged',
			this.switchBoxChanged,
		);
		eventBus.off(
			'headerSwitchBoxChanged',
			this.headerSwitchBoxChanged,
		);
		this.table?.destroy();
	}

	protected groupIdFilterFunction(): void {
		const Allfiltered = this.rowData.filter((data) => {
			if (data.groupid !== undefined) {
				return data.groupid === this.search;
			}
			return data;
		});
		this.table?.setData(Allfiltered);
		const allChecked = this.table?.getData().every((item) => this.checkOffering[item.id]);
		if (allChecked) {
			this.updateHeaderBox(true);
		} else {
			this.updateHeaderBox(false);
		}
	}

	@Watch('loggedIn')
	private async getData(): Promise<void> {
		if (this.table) {
			const offeringParameter = new URLSearchParams({
				limit: '0',
			});

			const badgeOfferingParameter = new URLSearchParams({
				where: JSON.stringify({ badgeid: this.routeId }),
				limit: '0',
			});

			this.table.alert('Loading');
			try {
				const [offeringData, badgeOfferingData] = await Promise.all([
					OfferingService.getAll(offeringParameter),
					BadgeOfferingService.getAll(badgeOfferingParameter),
				]);

				this.rowData = offeringData;
				this.checkOffering = Object.fromEntries(
					badgeOfferingData.map((item) => [item.offeringid, item]),
				);

				this.table.setData(this.rowData);
			} catch (error: any) {
				this.$toastError(error.message);
			} finally {
				this.table.clearAlert();
			}
		}
	}
}

export default toNative(OfferingTable);
